import React, { useRef, useState } from 'react';
import { useEffect } from 'react';
import {
  LTBox,
  LTButton,
  LTLoader,
  LTText,
} from '@lambdatestincprivate/lt-components';
import { useDispatch, useSelector } from 'react-redux';
import { LinkExternalIcon } from '@primer/octicons-react';
import { useTheme } from 'styled-components';
import DoughnutChart from './DoughnutChart';
import IssueSeverityIcon from '../../assets/accessibility/IssueSeverityIcon';
import ExclamationIcon from '../../assets/accessibility/ExclamationIcon';
import AccessibilitySummary from './AccessibilitySummary';
import AccessibilityPlanError from './AccessibilityPlanError';
import ZeroAccessibilityIssues from './ZeroAccessibilityIssues';
import CapabilityScreen from '../../common/CapabilityScreen';
import {
  AvailableCapabilities,
  CapabilityDocumentationLabel,
  CapabilityDocumentationLink,
} from '../../constants/View';
import { isEmpty } from 'lodash';
import { IssueTypes } from './constants';
import { isCapabilitySet } from '../../utils/testHelpers';
import { getUrlByUserDomain, getShareId } from '../../utils/utilityFunctions';
import { resetAccessibilityDetails } from '../../redux/slices/accessibility';
import { fetchAccessibilityDetails } from '../../redux/thunk/accessibility';
import { useQueryParams } from '../../hooks/useQueryParams';
import { getEnv } from '../../utils/env';

const getTotalSum = (arr) => {
  return arr.reduce((sum, obj) => sum + (obj?.value || 0), 0);
};

const IssueTypeColorMap = {
  Minor: '#333',
  Critical: '#CF222E',
  Serious: '#8250DF',
  Moderate: '#0969DA',
};

let accessibilityPollingCounter = 0;

// Maximum number of polling attempts
const POLLING_LIMIT = 5;

// Time interval (in ms) between each polling attempt
const POLLING_TIME = 5000;

const Accessibility = ({ testDetails, userInfo, sharedDataCount }) => {
  const [isPolling, setIsPolling] = useState(false);
  const accessibilityDetails = useSelector((state) => state.accessibility);
  const dispatch = useDispatch();
  const theme = useTheme();
  const searchParams = useQueryParams();

  const pollingIntervalRef = useRef(null);

  let capability = '';
  if (testDetails?.capability) {
    try {
      capability = JSON.parse(testDetails.capability);
    } catch (error) {
      capability = {};
    }
  }
  const isAccessibilityCapabilitySet = isCapabilitySet({
    capability,
    testType: testDetails.type,
    type: 'accessibility',
  });
  const shareId = getShareId();
  const showAccessibility = searchParams.get('showAccessibility');
  const { details: accessibilityData, error = '', loading } =
    accessibilityDetails || {};
  const { id: testId, statusInd = '' } = testDetails;
  const accessibilityTestId = `AUT_${testId}`;

  const organization = userInfo?.data?.organization || {};
  const planAttributes = organization?.plan_attributes;
  // Check if accessibility is allowed based on test details feature flag
  // This enables viewing past reports even when the plan is exhausted
  const isAccessibilityAllowedOnTest =
    testDetails?.feature_flag?.accessibilityAllowed;
  // Get the reason if accessibility is blocked (for showing appropriate message)
  const accessibilityBlockedReason =
    testDetails?.feature_flag?.accessibilityBlockedReason;
  // Fall back to plan attribute check for backward compatibility
  const isAccessibilityPlanSet = shareId
    ? showAccessibility
    : planAttributes['AUTOMATION_ACCESSIBILITY_ENABLED'] &&
      planAttributes['AUTOMATION_ACCESSIBILITY_ENABLED'] === 1;

  const getTestQueryParams = () => {
    let queryParams = {
      testId: accessibilityTestId,
    };
    if (sharedDataCount > 1) {
      queryParams.buildId = testDetails.buildId;
    }
    return queryParams;
  };

  const startAccessibilityPolling = () => {
    if (pollingIntervalRef.current) {
      return;
    }
    setIsPolling(true);
    pollingIntervalRef.current = setInterval(() => {
      // Prevent parallel requests
      if (loading) {
        return;
      }

      accessibilityPollingCounter++;
      if (accessibilityPollingCounter >= POLLING_LIMIT - 1) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        accessibilityPollingCounter = 0;
        setIsPolling(false);
      }
      // reset error and fetch accessibilityData
      dispatch(resetAccessibilityDetails());
      dispatch(
        fetchAccessibilityDetails({
          shareId,
          queryParams: getTestQueryParams(),
        })
      );
    }, POLLING_TIME);
  };

  useEffect(() => {
    return () => {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      accessibilityPollingCounter = 0;
      setIsPolling(false);
    };
  }, []);

  // Combined check: capability AND (accessibilityAllowed OR plan)
  // Capability is always required, then either new flow (accessibilityAllowed) or old flow (plan)
  const isAccessibilityEnabled =
    isAccessibilityCapabilitySet &&
    (isAccessibilityAllowedOnTest || isAccessibilityPlanSet);

  // Show custom blocked reason message if accessibilityBlockedReason is set
  const showBlockedReasonMessage = !!accessibilityBlockedReason;

  useEffect(() => {
    if (
      statusInd !== 'running' &&
      statusInd !== 'queued' &&
      isAccessibilityEnabled &&
      !showBlockedReasonMessage &&
      isEmpty(accessibilityData)
    ) {
      if (!pollingIntervalRef.current) {
        startAccessibilityPolling();
      }
    }
  }, [statusInd, testId]);

  useEffect(() => {
    if (!isEmpty(accessibilityData)) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        accessibilityPollingCounter = 0;
        setIsPolling(false);
      }
    }
  }, [accessibilityData]);

  useEffect(() => {
    dispatch(resetAccessibilityDetails());

    if (
      isAccessibilityEnabled &&
      !showBlockedReasonMessage &&
      statusInd !== 'running' &&
      statusInd !== 'queued'
    ) {
      dispatch(
        fetchAccessibilityDetails({
          shareId,
          queryParams: getTestQueryParams(),
        })
      );
    }
  }, [testId]);

  const goToAccessibilityDashboard = () => {
    const env = getEnv();
    window.open(
      `${getUrlByUserDomain(
        env.REACT_APP_ACCESSIBILITY
      )}/automation/test/AUT_${testId}`,
      '_blank'
    );
  };

  const issueSummary = Object.values(IssueTypes).map((type) => ({
    label: type,
    color: IssueTypeColorMap?.[type],
    value: accessibilityData?.[`${type.toLowerCase()}_issues`] || 0,
  }));

  const totalIssuesCount = getTotalSum(issueSummary);

  return (
    <LTBox id="test-detail-accessibility" data-testid="accessibility-tab-container" width="100%" height="100%">
      {showBlockedReasonMessage && (
        <AccessibilityPlanError
          blockedReason={accessibilityBlockedReason}
        />
      )}

      {!showBlockedReasonMessage && !isAccessibilityEnabled && (
        <LTBox className="noAccessibilityBody" data-testid="accessibility-not-enabled" width="100%" height="100%">
          <CapabilityScreen
            docsLink={
              CapabilityDocumentationLink[AvailableCapabilities.ACCESSIBILITY]
            }
            docsLabel={
              CapabilityDocumentationLabel[AvailableCapabilities.ACCESSIBILITY]
            }
            capabilityName={AvailableCapabilities.ACCESSIBILITY}
            capabilityLabel={'Accessibility Report'}
            isMissingCapability={true}
            testType={testDetails.type}
          />
        </LTBox>
      )}

      {!showBlockedReasonMessage && isAccessibilityEnabled && error && !isPolling && (
        <>
          <LTBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="4px"
            width="100%"
            height="100%"
            padding="24px"
          >
            <ExclamationIcon />
            <LTText
              fontSize={LTText.FontSize.FONT_14}
              color="fg.muted"
              textAlign="center"
            >
              Something went wrong while fetching accessibility report data.
            </LTText>
          </LTBox>
        </>
      )}

      {!showBlockedReasonMessage && isAccessibilityEnabled && isPolling && (
        <LTLoader
          size={LTLoader.SpinnerSizes.LARGE}
          heading="Generating Accessibility Report"
        />
      )}

      {!showBlockedReasonMessage && isAccessibilityEnabled && accessibilityData && totalIssuesCount > 0 && (
        <LTBox
          maxWidth="1216px"
          margin="0 auto"
          padding="24px"
          height="100%"
          overflowY="auto"
        >
          <LTBox
            display="flex"
            justifyContent="space-between"
            gap="24px"
            paddingBottom="16px"
            borderBottom="1px solid"
            borderColor={theme?.colors?.border?.default}
          >
            <LTBox>
              <LTText data-testid="accessibility-summary-heading" fontSize={LTText.FontSize.FONT_20} fontWeight="600">
                Accessibility Summary
              </LTText>
              <LTText
                fontSize={LTText.FontSize.FONT_14}
                fontWeight="400"
                color="fg.muted"
              >
                Automatically assess your entire webpage for accessibility
                compliance
              </LTText>
            </LTBox>

            {!shareId && (
              <LTButton
                data-testid="accessibility-get-full-report-btn"
                label="Get Full Report"
                trailingIcon={<LinkExternalIcon size={16} />}
                size={LTButton.Size.MEDIUM}
                variant={LTButton.Variant.DEFAULT}
                onClick={goToAccessibilityDashboard}
              />
            )}
          </LTBox>

          <AccessibilitySummary totalIssuesCount={totalIssuesCount} />

          <LTBox
            flex="1"
            border="1px solid"
            borderColor={theme?.colors?.border?.default}
            borderRadius="6px"
            padding="16px"
            height="fit-content"
            marginTop="24px"
          >
            <LTBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <LTText
                as="span"
                fontSize={LTText.FontSize.FONT_14}
                fontWeight="400"
              >
                Most Severe
              </LTText>
              <LTText
                as="span"
                fontSize={LTText.FontSize.FONT_20}
                fontWeight="600"
              >
                {`${totalIssuesCount} `}
                <LTText
                  as="span"
                  fontSize={LTText.FontSize.FONT_16}
                  fontWeight="400"
                  color="fg.muted"
                >
                  Issues
                </LTText>
              </LTText>
            </LTBox>
            <LTBox
              padding="8px 24px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              marginTop="16px"
              gridGap="48px"
            >
              <DoughnutChart
                issueSummary={issueSummary}
                totalCount={totalIssuesCount}
              />
              <LTBox display="flex" flexDirection="column" gridGap="8px">
                {issueSummary.map((issueData) => (
                  <LTBox
                    padding="8px"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom="1px solid"
                    borderColor={theme?.colors?.border?.default}
                    width="180px"
                  >
                    <LTText
                      as="span"
                      fontSize={LTText.FontSize.FONT_14}
                      fontWeight="400"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <IssueSeverityIcon severity={issueData?.label} />
                      {issueData?.label}
                    </LTText>

                    <LTText
                      as="span"
                      fontSize={LTText.FontSize.FONT_12}
                      fontWeight="600"
                    >
                      {issueData?.value || '0'}
                    </LTText>
                  </LTBox>
                ))}
              </LTBox>
            </LTBox>
          </LTBox>
        </LTBox>
      )}

      {!showBlockedReasonMessage &&
        isAccessibilityEnabled &&
        accessibilityData &&
        totalIssuesCount === 0 && <ZeroAccessibilityIssues />}
    </LTBox>
  );
};

export default Accessibility;
